import React, { useState, useRef, useEffect, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import { useChat } from '../contexts/ChatContext';
import {
  Send,
  Bot,
  User,
  FileText,
  ExternalLink,
  Download,
  BookOpen,
  Clock,
  CheckCircle,
  AlertTriangle,
  Brain,
  Target,
  Activity,
  Microscope,
  Settings
} from 'lucide-react';

const API_BASE_URL = '/api';

const ModernChatPage = () => {
  const {
    messages,
    setMessages,
    sessionId,
    setSessionId,
    isLoading,
    setIsLoading,
    addMessage,
    clearMessages,
    newSession
  } = useChat();
  const [inputMessage, setInputMessage] = useState('');
  const [chatMode, setChatMode] = useState('chat');
  const [searchOptions, setSearchOptions] = useState({
    top_k: 5,
    use_reranking: true,
    include_parent_context: true,
    use_hybrid: null,
    use_llm: true,
    use_hybrid_search: true,
    max_history_messages: 5,
    max_history_tokens: 4000
  });

  const updateSearchOption = (key, value) => {
    setSearchOptions(prev => ({ ...prev, [key]: value }));
  };
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Load history only once when component mounts and sessionId is initially set
    if (sessionId && chatMode === 'chat' && messages.length === 0) {
      loadChatHistory();
    }
  }, [sessionId, chatMode, messages.length]);

  const loadChatHistory = async () => {
    if (!sessionId) return;

    try {
      const response = await axios.get(`${API_BASE_URL}/chat/history/${sessionId}`);
      const history = response.data.history || [];

      const historyMessages = history.map((msg, index) => ({
        id: `history-${index}`,
        content: msg.content,
        role: msg.role,
        timestamp: msg.timestamp ? new Date(msg.timestamp * 1000) : new Date(),
        isFromHistory: true
      }));

      setMessages(historyMessages);
    } catch (error) {
      console.warn('Impossible de charger l\'historique:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      content: inputMessage,
      role: 'user',
      timestamp: new Date(),
    };

    addMessage(userMessage);
    setInputMessage('');
    setIsLoading(true);

    try {
      let response;

      if (chatMode === 'chat') {
        response = await axios.post(`${API_BASE_URL}/chat`, {
          message: inputMessage,
          session_id: sessionId,
          max_history_messages: searchOptions.max_history_messages,
          max_history_tokens: searchOptions.max_history_tokens,
          top_k: searchOptions.top_k,
          use_reranking: searchOptions.use_reranking,
          include_parent_context: searchOptions.include_parent_context,
          use_hybrid: searchOptions.use_hybrid,
          use_llm: searchOptions.use_llm,
          use_hybrid_search: searchOptions.use_hybrid_search
        });

        if (response.data.session_id && !sessionId) {
          setSessionId(response.data.session_id);
        }
      } else {
        response = await axios.post(`${API_BASE_URL}/query`, {
          query: inputMessage,
          use_llm: searchOptions.use_llm,
          use_hybrid_search: searchOptions.use_hybrid_search
        }, {
          params: {
            top_k: searchOptions.top_k,
            use_reranking: searchOptions.use_reranking,
            include_parent_context: searchOptions.include_parent_context,
            use_hybrid: searchOptions.use_hybrid
          }
        });
      }

      const botMessage = {
        id: `bot-${Date.now()}`,
        content: response.data.response || response.data.answer || "Désolé, je n'ai pas pu générer de réponse.",
        role: 'assistant',
        timestamp: new Date(),
        sources: response.data.sources || [],
        metadata: response.data.response_metadata,
        search_metadata: response.data.search_metadata,
        sessionId: response.data.session_id
      };

      console.log("=== DEBUG CHAT ===");
      console.log("Message bot créé:", botMessage);
      console.log("Sources reçues:", botMessage.sources);
      console.log("Nombre de sources:", botMessage.sources.length);
      console.log("Type de sources:", typeof botMessage.sources);
      console.log("Response data complète:", response.data);
      console.log("=== FIN DEBUG ===");

      // Create a clean copy without JSON serialization to preserve Date objects
      const botMessageCopy = {
        ...botMessage,
        sources: [...botMessage.sources] // Shallow copy of sources array
      };
      addMessage(botMessageCopy);
    } catch (error) {
      const errorMessage = {
        id: `error-${Date.now()}`,
        content: `Erreur lors de la communication avec l'API: ${error.response?.data?.detail || error.message}`,
        role: 'assistant',
        timestamp: new Date(),
        isError: true
      };
      addMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewSession = () => {
    newSession();
  };

  const handleClearHistory = async () => {
    if (!sessionId) {
      clearMessages();
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/chat/history/${sessionId}`);
      clearMessages();
    } catch (error) {
      console.error('Erreur lors de l\'effacement de l\'historique:', error);
      clearMessages();
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatConfidence = (confidence) => {
    if (typeof confidence !== 'number') return 'N/A';
    return `${Math.round(confidence * 100)}%`;
  };

  const downloadDocument = async (fileName) => {
    try {
      const downloadUrl = `${API_BASE_URL}/documents/${encodeURIComponent(fileName)}/download`;

      const response = await fetch(downloadUrl, {
        method: 'HEAD'
      });

      if (response.ok) {
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.target = '_blank';
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert(`Le document "${fileName}" n'est pas disponible au téléchargement.`);
      }
    } catch (error) {
      console.error("Erreur lors du téléchargement:", error);
      alert(`Erreur lors du téléchargement: ${error.message}`);
    }
  };

  // Helper functions for document deduplication
  const getUniqueDocuments = (sources) => {
    console.log("DEBUG: getUniqueDocuments called with sources:", sources);
    const uniqueDocs = new Map();

    sources.forEach((source, index) => {
      const fileName = source.metadata?.file_name;
      console.log(`DEBUG: Processing source ${index}, fileName:`, fileName);
      if (fileName && !uniqueDocs.has(fileName)) {
        // Find all source indices for this file
        const sourceIndices = sources
          .map((s, i) => ({ fileName: s.metadata?.file_name, index: i }))
          .filter(item => item.fileName === fileName)
          .map(item => item.index + 1); // +1 pour afficher "source 1" au lieu de "source 0"

        const doc = {
          fileName: fileName,
          title: source.metadata?.pdf_title,
          author: source.metadata?.pdf_author,
          pageCount: source.metadata?.total_pages,
          sourceReferences: sourceIndices, // Tableau des numéros de source
          sources: sources.filter(s => s.metadata?.file_name === fileName)
        };
        console.log(`DEBUG: Adding document:`, doc);
        uniqueDocs.set(fileName, doc);
      }
    });

    const result = Array.from(uniqueDocs.values());
    console.log("DEBUG: getUniqueDocuments returning:", result);
    return result;
  };

  const getUniqueDocumentsCount = (sources) => {
    const uniqueFileNames = new Set();
    sources.forEach(source => {
      const fileName = source.metadata?.file_name;
      if (fileName) {
        uniqueFileNames.add(fileName);
      }
    });
    return uniqueFileNames.size;
  };

  const SourceCard = ({ source, index }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 text-sm">
              Source {index + 1}
            </h4>
            <p className="text-xs text-gray-600">
              {source.metadata?.file_name || 'Document inconnu'}
            </p>
          </div>
        </div>
        {source.metadata?.file_name && (
          <button
            onClick={() => downloadDocument(source.metadata.file_name)}
            className="flex items-center space-x-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs font-medium transition-colors"
          >
            <Download className="w-3 h-3" />
            <span>Ouvrir PDF</span>
          </button>
        )}
      </div>

      <div className="space-y-2 text-xs text-gray-600">
        {source.metadata?.pdf_title && (
          <div className="flex items-center space-x-2">
            <FileText className="w-3 h-3" />
            <span className="font-medium">Titre:</span>
            <span>{source.metadata.pdf_title}</span>
          </div>
        )}
        {source.metadata?.page_number && (
          <div className="flex items-center space-x-2">
            <span className="font-medium">Page:</span>
            <span>{source.metadata.page_number}</span>
          </div>
        )}
        {source.metadata?.authors && (
          <div className="flex items-center space-x-2">
            <span className="font-medium">Auteurs:</span>
            <span>{Array.isArray(source.metadata.authors) ? source.metadata.authors.join(', ') : source.metadata.authors}</span>
          </div>
        )}
        {source.metadata?.evidence_level && (
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-3 h-3 text-green-600" />
            <span className="font-medium">Niveau de preuve:</span>
            <span className="text-green-600">{source.metadata.evidence_level}</span>
          </div>
        )}
        {source.metadata?.journal && (
          <div className="flex items-center space-x-2">
            <span className="font-medium">Journal:</span>
            <span>{source.metadata.journal}</span>
          </div>
        )}
        {source.metadata?.publication_date && (
          <div className="flex items-center space-x-2">
            <span className="font-medium">Année:</span>
            <span>{source.metadata.publication_date}</span>
          </div>
        )}
        {source.metadata?.medical_specialty && (
          <div className="flex items-center space-x-2">
            <Microscope className="w-3 h-3 text-purple-600" />
            <span className="font-medium">Spécialité:</span>
            <span className="text-purple-600">{source.metadata.medical_specialty}</span>
          </div>
        )}
      </div>

      <div className="mt-3 pt-3 border-t border-gray-100">
        <p className="text-xs text-gray-700 leading-relaxed">
          {source.text}
        </p>
      </div>

      {source.score && (
        <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
          <span>Score de pertinence: {Math.round(source.score * 100)}%</span>
          <div className="flex items-center space-x-1">
            <Clock className="w-3 h-3" />
            <span>ID: {source.node_id?.slice(-8)}</span>
          </div>
        </div>
      )}
    </div>
  );

  const MessageBubble = ({ message }) => {
    const isUser = message.role === 'user';
    const isAssistant = message.role === 'assistant';

    return (
      <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
        <div className={`max-w-3xl ${isUser ? 'order-2' : 'order-1'}`}>
          <div className={`flex items-end space-x-2 ${isUser ? 'flex-row-reverse space-x-reverse' : ''}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
              isUser ? 'bg-blue-600' : 'bg-green-600'
            }`}>
              {isUser ? (
                <User className="w-4 h-4 text-white" />
              ) : (
                <Bot className="w-4 h-4 text-white" />
              )}
            </div>

            <div className={`rounded-2xl px-4 py-3 min-h-0 ${
              isUser
                ? 'bg-blue-600 text-white rounded-br-sm'
                : 'bg-gray-100 text-gray-900 rounded-bl-sm'
            }`}>
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-sm font-medium">
                  {isUser ? 'Vous' : 'Assistant'}
                </span>
                {message.isFromHistory && (
                  <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs">
                    📜 Historique
                  </span>
                )}
                <span className={`text-xs ${isUser ? 'text-blue-100' : 'text-gray-500'}`}>
                  {formatTimestamp(message.timestamp)}
                </span>
              </div>

              <div className={`text-sm leading-relaxed min-h-0 ${
                isUser ? 'text-blue-50' : 'text-gray-800'
              }`}>
                {isAssistant ? (
                  <ReactMarkdown
                    className="prose prose-sm max-w-none"
                    components={{
                      a: ({ href, children }) => (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          {children}
                        </a>
                      ),
                      code: ({ children }) => (
                        <code className="bg-gray-200 text-gray-800 px-1 py-0.5 rounded text-sm">
                          {children}
                        </code>
                      ),
                      strong: ({ children }) => (
                        <strong className="font-semibold">
                          {children}
                        </strong>
                      )
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                ) : (
                  message.content
                )}
              </div>

              {message.metadata && (
                <div className={`mt-3 pt-3 border-t ${
                  isUser ? 'border-blue-400' : 'border-gray-200'
                } space-y-2`}>
                  <div className="flex flex-wrap gap-2">
                    <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium ${
                      isUser
                        ? 'bg-blue-500 text-white'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      <Target className="w-3 h-3" />
                      <span>Confiance: {formatConfidence(message.metadata.confidence)}</span>
                    </span>
                    {message.metadata.evidence_level && (
                      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium ${
                        isUser
                          ? 'bg-green-500 text-white'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        <CheckCircle className="w-3 h-3" />
                        <span>Preuve: {message.metadata.evidence_level}</span>
                      </span>
                    )}
                    <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium ${
                      isUser
                        ? 'bg-gray-300 text-gray-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      <Clock className="w-3 h-3" />
                      <span>Temps: {message.metadata.processing_time?.toFixed(2)}s</span>
                    </span>
                  </div>
                  {message.metadata.medical_disclaimer && (
                    <div className={`text-xs italic flex items-start space-x-1 ${
                      isUser ? 'text-blue-100' : 'text-yellow-700'
                    }`}>
                      <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      <span>{message.metadata.medical_disclaimer}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto p-2 sm:p-4">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-4 sm:mb-6">
          <div className="p-3 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
                  <Microscope className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Assistant PaperFlow</h1>
                  <p className="text-sm sm:text-base text-gray-600">Recherche intelligente dans des articles scientifiques</p>
                </div>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-3 mt-3 sm:mt-0">
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                  chatMode === 'chat'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-blue-100 text-blue-800'
                }`}>
                  {chatMode === 'chat' ? '🧠 Conversation' : '🔍 Requête'}
                </div>
                {sessionId && (
                  <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-lg text-xs font-mono">
                    {sessionId.substring(0, 8)}...
                  </span>
                )}
              </div>
            </div>

            {/* Controls */}
            <div className="space-y-4">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex items-center space-x-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      value="chat"
                      checked={chatMode === 'chat'}
                      onChange={(e) => setChatMode(e.target.value)}
                      className="text-blue-600"
                    />
                    <span className="flex items-center space-x-2 text-sm">
                      <Brain className="w-4 h-4" />
                      <span>Chat avec mémoire</span>
                    </span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      value="query"
                      checked={chatMode === 'query'}
                      onChange={(e) => setChatMode(e.target.value)}
                      className="text-blue-600"
                    />
                    <span className="flex items-center space-x-2 text-sm">
                      <Target className="w-4 h-4" />
                      <span>Requête simple</span>
                    </span>
                  </label>
                </div>

                {chatMode === 'chat' && (
                  <div className="flex items-center space-x-2 ml-auto">
                    <button
                      onClick={handleNewSession}
                      className="flex items-center space-x-1 px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm transition-colors"
                    >
                      <span>Nouvelle session</span>
                    </button>
                    <button
                      onClick={handleClearHistory}
                      className="flex items-center space-x-1 px-3 py-1 bg-red-100 hover:bg-red-200 rounded-lg text-sm transition-colors"
                    >
                      <span>Effacer</span>
                    </button>
                    {sessionId && (
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-lg text-xs font-mono">
                        {sessionId.substring(0, 8)}...
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Search Options */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                {chatMode === 'chat' && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-gray-700 text-sm flex items-center">
                      <Brain className="w-4 h-4 mr-2" />
                      Mémoire conversationnelle
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-gray-600">Messages</label>
                        <input
                          type="number"
                          min="1"
                          max="50"
                          value={searchOptions.max_history_messages}
                          onChange={(e) => updateSearchOption('max_history_messages', parseInt(e.target.value))}
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600">Tokens</label>
                        <input
                          type="number"
                          min="1000"
                          max="10000"
                          step="500"
                          value={searchOptions.max_history_tokens}
                          onChange={(e) => updateSearchOption('max_history_tokens', parseInt(e.target.value))}
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <h4 className="font-medium text-gray-700 text-sm flex items-center">
                    <Activity className="w-4 h-4 mr-2" />
                    Options de recherche
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-gray-600">Documents</label>
                      <input
                        type="number"
                        min="1"
                        max="20"
                        value={searchOptions.top_k}
                        onChange={(e) => updateSearchOption('top_k', parseInt(e.target.value))}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Mode</label>
                      <select
                        value={searchOptions.use_hybrid === null ? '' : searchOptions.use_hybrid}
                        onChange={(e) => updateSearchOption('use_hybrid', e.target.value === '' ? null : e.target.value === 'true')}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Auto</option>
                        <option value="true">Hybride</option>
                        <option value="false">Vecteur</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="font-medium text-gray-700 text-sm flex items-center">
                    <Settings className="w-4 h-4 mr-2" />
                    Options avancées
                  </h4>
                  <div className="space-y-1">
                    <label className="flex items-center space-x-2 text-xs">
                      <input
                        type="checkbox"
                        checked={searchOptions.use_reranking}
                        onChange={(e) => updateSearchOption('use_reranking', e.target.checked)}
                        className="text-blue-600"
                      />
                      <span>Re-ranking</span>
                    </label>
                    <label className="flex items-center space-x-2 text-xs">
                      <input
                        type="checkbox"
                        checked={searchOptions.include_parent_context}
                        onChange={(e) => updateSearchOption('include_parent_context', e.target.checked)}
                        className="text-blue-600"
                      />
                      <span>Contexte parent</span>
                    </label>
                    <label className="flex items-center space-x-2 text-xs">
                      <input
                        type="checkbox"
                        checked={searchOptions.use_hybrid_search}
                        onChange={(e) => updateSearchOption('use_hybrid_search', e.target.checked)}
                        className="text-blue-600"
                      />
                      <span>Recherche hybride</span>
                    </label>
                    <label className="flex items-center space-x-2 text-xs">
                      <input
                        type="checkbox"
                        checked={searchOptions.use_llm}
                        onChange={(e) => updateSearchOption('use_llm', e.target.checked)}
                        className="text-blue-600"
                      />
                      <span>LLM</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Chat Messages */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="h-[800px] overflow-y-auto p-6 space-y-4">
            {messages.length === 0 && (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                  <Microscope className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-lg">
                    {chatMode === 'chat'
                      ? 'Bienvenue ! Je suis votre assistant PaperFlow avec mémoire conversationnelle.'
                      : 'Bienvenue ! Je suis votre assistant PaperFlow.'
                    }
                  </p>
                  <p className="text-sm mt-2">
                    Posez-moi des questions sur les articles scientifiques que vous avez ingérés.
                  </p>
                </div>
              </div>
            )}

            {messages.map((message) => {
              console.log("Rendering message:", message.id, "sources:", message.sources);
              return (
                <div key={message.id}>
                  <MessageBubble message={message} />

                  {/* Sources Section - Only for assistant responses */}
                  {message.role === 'assistant' && Array.isArray(message.sources) && message.sources.length > 0 && (
                    <div className="ml-12 mb-6">
                      <div className="flex items-center space-x-2 mb-4">
                        <FileText className="w-4 h-4 text-gray-600" />
                        <h3 className="font-semibold text-gray-900">
                          Documents sources ({getUniqueDocumentsCount(message.sources)})
                        </h3>
                      </div>
                      <div className="space-y-2">
                        {getUniqueDocuments(message.sources).map((doc, index) => (
                          <div key={index} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900 text-sm">
                                {doc.title || doc.fileName || `Document ${index + 1}`}
                                {doc.sourceReferences && doc.sourceReferences.length > 0 && (
                                  <span className="text-gray-500 font-normal ml-2">
                                    (source {doc.sourceReferences.join(', ')})
                                  </span>
                                )}
                              </h4>
                              {doc.author && (
                                <p className="text-xs text-gray-600 mt-1">{doc.author}</p>
                              )}
                              {doc.pageCount && (
                                <p className="text-xs text-gray-500">{doc.pageCount} pages</p>
                              )}
                            </div>
                            <button
                              onClick={() => downloadDocument(doc.fileName)}
                              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                            >
                              <Download className="w-4 h-4" />
                              <span>Ouvrir PDF</span>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 text-gray-900 rounded-2xl rounded-bl-sm px-4 py-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                      <span className="text-gray-600 text-sm">
                        {chatMode === 'chat' ? 'Recherche avec contexte...' : 'Recherche en cours...'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input Area */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <form onSubmit={handleSendMessage} className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-3 sm:space-y-0">
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder={chatMode === 'chat' ?
                  "Posez votre question... (je me souviendrai du contexte)" :
                  "Posez votre question..."
                }
                rows={3}
                disabled={isLoading}
                className="flex-1 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm sm:text-base"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (inputMessage.trim() && !isLoading) {
                      handleSendMessage(e);
                    }
                  }
                }}
              />
              <button
                type="submit"
                disabled={!inputMessage.trim() || isLoading}
                className="sm:self-end px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 transition-all duration-200 flex items-center justify-center space-x-1 sm:space-x-2 text-sm sm:text-base w-full sm:w-auto"
              >
                <Send className="w-4 h-4" />
                <span>{isLoading ? 'Envoi...' : 'Envoyer'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ModernChatPage;